import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  vatTuSearchColumns,
  vuViecSearchColumns
} from '@/constants';
import { AccountModel, BoPhan, ChiPhi, DotThanhToan, HopDong, KheUoc, Phi, VatTu, VuViec } from '@/types/schemas';
import type { InputTableColumn } from '@/components/custom/erp/input-table-v2';
import { CellFieldV2, SearchField } from '@/components/custom/erp';
import { formatMoney, formatNumber } from '@/lib/formatUtils';

export const getDetailTableColumns = (
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void
): InputTableColumn[] => [
  {
    field: 'ma_vt',
    headerName: 'Mã sản phẩm',
    width: 120,
    renderCell: params => params.row.ma_vt_data?.ma_vt
  },
  {
    field: 'ten_vt',
    headerName: 'Tên sản phẩm',
    width: 200,
    renderCell: params => params.row.ma_vt_data?.ten_vt
  },
  {
    field: 'dvt',
    headerName: 'Đơn vị tính',
    width: 100,
    renderCell: params => params.row.dvt_data?.ten_dvt
  },
  {
    field: 'ma_kho',
    headerName: 'Mã kho',
    width: 120,
    renderCell: params => params.row.ma_kho_data?.ma_kho
  },
  {
    field: 'ma_vi_tri',
    headerName: 'Mã vị trí',
    width: 120,
    renderCell: params => params.row.ma_vi_tri_data?.ma_vi_tri
  },
  {
    field: 'so_luong',
    headerName: 'Số lượng',
    width: 120,
    type: 'number',
    renderCell: params => formatNumber(params.row.so_luong)
  },
  {
    field: 'tien0',
    headerName: 'Tiền hàng',
    type: 'number',
    width: 120,
    renderCell: params => formatMoney(params.row.tien0)
  },
  {
    field: 'tien3',
    headerName: 'Tiền tính thuế nk',
    width: 120,
    renderCell: params => formatMoney(params.row.tien3)
  },
  {
    field: 'cp_nt',
    headerName: 'Chi phí VND',
    width: 120,
    renderCell: params => (
      <CellFieldV2
        name='cp_nt'
        type='number'
        value={params.row.cp_nt || 0}
        commitOnBlur
        onValueChange={value => onCellValueChange(params.row.uuid, 'cp_nt', value)}
      />
    )
  },
  {
    field: 'tk_vt',
    headerName: 'TK nợ',
    width: 120,
    renderCell: params => (
      <SearchField<AccountModel>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
        searchColumns={accountSearchColumns}
        columnDisplay='code'
        dialogTitle='Danh mục tài khoản'
        value={params.row.tk_vt_data?.code || params.row.ma_vt_data?.tk_khcp_data?.code || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'tk_vt_data', row)}
      />
    )
  },
  {
    field: 'thue_nt',
    headerName: 'Thuế GTGT VND',
    width: 120,
    renderCell: params => <CellFieldV2 name='thue_nt' type='number' value={params.row.thue_nt || 0} disabled />
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: params => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
      />
    )
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: params => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vu_viec'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vu_viec || params.row.ma_vv_data?.ma_vv || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
      />
    )
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: params => (
      <SearchField<HopDong>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        columnDisplay='ma_hd'
        dialogTitle='Danh mục hợp đồng'
        value={params.row.ma_hd_data?.ma_hd || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
      />
    )
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 120,
    renderCell: params => (
      <SearchField<DotThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục đợt thanh toán'
        value={params.row.ma_dtt_data?.ma_dtt || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
      />
    )
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: params => (
      <SearchField<KheUoc>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khế ước'
        value={params.row.ma_ku_data?.ma_ku || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
      />
    )
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: params => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
      />
    )
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: params => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTuSearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục sản phẩm'
        value={params.row.ma_sp_data?.ma_vt || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
      />
    )
  },
  {
    field: 'ma_lsx',
    headerName: 'Lệnh sản xuất',
    width: 120,
    renderCell: params => (
      <SearchField<MuaHangRecord>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI}/`}
        searchColumns={chiPhiSearchColumns}
        columnDisplay='ma_lsx'
        dialogTitle='Lệnh sản xuất'
        value={params.row.ma_lsx_data?.ma_lsx || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
      />
    )
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 150,
    renderCell: params => (
      <SearchField<ChiPhi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI}/`}
        searchColumns={chiPhiSearchColumns}
        columnDisplay='ma_cp'
        dialogTitle='Danh mục chi phí không hợp lệ'
        value={params.row.ma_cp0_data?.ma_cp || ''}
        onRowSelection={(row: MuaHangRecord) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
      />
    )
  },
  {
    field: 'so_ct_hd4',
    headerName: 'Số hóa đơn mua trong nước',
    width: 190,
    renderCell: params => params.row.id_hd?.so_ct || ''
  },
  // {
  //   field: 'so_ct_hd5',
  //   headerName: 'Số hóa đơn mua nhập khẩu',
  //   width: 150,
  //   renderCell: params => <CellField name='so_ct_hd5' type='text' value={params.row.so_ct_hd5 || ''} />
  // },
  // {
  //   field: 'so_ct_hd7',
  //   headerName: 'Số hóa đơn mua xuất thẳng',
  //   width: 150,
  //   renderCell: params => <CellField name='so_ct_hd5' type='text' value={params.row.so_ct_hd5 || ''} />
  // },
  {
    field: 'line_hd',
    headerName: 'Dòng HĐ',
    width: 100,
    type: 'number',
    renderCell: params => params.row.line_hd?.line || ''
  }
];
